import { JsonLd } from "../components/JsonLd";
import { useState } from "react";
import { Link } from "react-router-dom";
import { useLocalePath } from "../hooks/useLocalePath";
import { SEOHead } from "../components/SEOHead";
import { SiteFooter } from "../components/SiteFooter";
import {
  ArrowRight, ChevronDown, ChevronUp, Shield, Clock,
  AlertTriangle, CheckCircle2, FileText, Eye,
} from "lucide-react";

const FAQS = [
  {
    q: "Does actively reviewed mean approved?",
    a: "No. It means USCIS is reviewing your case. Approval may come later, but it is not guaranteed.",
  },
  {
    q: "Can it stay like this for months?",
    a: "Yes. Many applicants remain in this status for some time.",
  },
  {
    q: "Why did my case update twice?",
    a: "Sometimes USCIS refreshes internal actions without changing the final outcome.",
  },
  {
    q: "Should I contact USCIS immediately?",
    a: "Usually no, unless your case is outside normal processing times.",
  },
];

const NEXT_STATUSES = [
  { label: "Case Approved", icon: CheckCircle2, color: "text-emerald-600" },
  { label: "Request for Evidence (RFE)", icon: FileText, color: "text-amber-600" },
  { label: "Interview Scheduled", icon: Clock, color: "text-sky-600" },
  { label: "Card Being Produced", icon: CheckCircle2, color: "text-emerald-600" },
  { label: "No change for some time", icon: Clock, color: "text-slate-400" },
];

function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-4 px-4 py-3.5 text-left hover:bg-slate-50 transition-colors"
        data-testid={`faq-${q.slice(0, 20).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
      >
        <span className="font-medium text-sm text-slate-800">{q}</span>
        {open ? (
          <ChevronUp className="h-4 w-4 text-slate-400 flex-shrink-0" />
        ) : (
          <ChevronDown className="h-4 w-4 text-slate-400 flex-shrink-0" />
        )}
      </button>
      {open && (
        <div className="px-4 pb-4 text-sm text-slate-600 leading-relaxed border-t border-slate-100 pt-3">
          {a}
        </div>
      )}
    </div>
  );
}

export default function ActivelyReviewedPage() {
  const lp = useLocalePath();

  return (
    <div className="min-h-screen bg-white" data-testid="actively-reviewed-page">
      <SEOHead
        title={'What Does "Case Is Being Actively Reviewed" Mean? USCIS Explained (2026)'}
        description="Learn what Case Is Being Actively Reviewed means, what may happen next, how long it can take, and what you should do now."
      />

      {/* JSON-LD Article Schema */}
      <JsonLd data={{
            "@context": "https://schema.org",
            "@type": "Article",
            headline: "What Does \"Case Is Being Actively Reviewed\" Mean? USCIS Explained (2026)",
            description: "Learn what \"Case Is Being Actively Reviewed\" means, what may happen next, how long it can take, and what you should do now.",
            datePublished: "2026-04-13",
            dateModified: "2026-04-13",
            url: "https://immigrationguide.us/actively-reviewed-uscis-meaning",
            publisher: {
              "@type": "Organization",
              name: "ImmigrationGuide",
              url: "https://immigrationguide.us",
            },
            mainEntityOfPage: {
              "@type": "WebPage",
              "@id": "https://immigrationguide.us/actively-reviewed-uscis-meaning",
            },
          }} />

      {/* JSON-LD FAQ Schema */}
      <JsonLd data={{
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQS.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }} />

      {/* Hero */}
      <section className="bg-gradient-to-b from-slate-50 to-white py-14 md:py-20" data-testid="ar-hero">
        <div className="max-w-3xl mx-auto px-4">
          <div className="inline-flex items-center gap-2 bg-teal-50 text-teal-700 text-xs font-medium px-3 py-1.5 rounded-full mb-5">
            <Eye className="h-3.5 w-3.5" /> USCIS Status Guide
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 leading-tight mb-5" data-testid="ar-h1">
            What Does "Case Is Being Actively Reviewed" Mean?
          </h1>
          <p className="text-base md:text-lg text-slate-600 leading-relaxed mb-4">
            If your USCIS case status says <strong>Case Is Being Actively Reviewed</strong>, you are not alone. Many applicants see this update and immediately wonder whether it means approval, delay, or a problem.
          </p>
          <p className="text-base md:text-lg text-slate-600 leading-relaxed">
            In most cases, this status simply means USCIS has opened your file and started reviewing it. It usually signals movement on your case, but it does not automatically mean a final decision has been made.
          </p>
          <div className="mt-5 flex items-center gap-2 text-xs text-slate-500">
            <Shield className="h-3.5 w-3.5 text-teal-600" />
            <span>Last updated: April 2026 · Based on publicly available USCIS information</span>
          </div>
        </div>
      </section>

      {/* What this status usually means */}
      <article className="py-12 md:py-16">
        <div className="max-w-3xl mx-auto px-4 space-y-14">

          <section>
            <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-4">What this status usually means</h2>
            <p className="text-sm text-slate-600 leading-relaxed mb-4">
              When USCIS updates a case to <strong>Actively Reviewed</strong>, it often means one of these things:
            </p>
            <ul className="space-y-2.5">
              {[
                "An officer has looked at your file",
                "Background checks are moving forward",
                "Biometrics were received and matched",
                "The case entered a decision workflow",
                "Internal processing activity has started",
              ].map((item) => (
                <li key={item} className="flex items-start gap-3 text-sm text-slate-600">
                  <CheckCircle2 className="h-4 w-4 text-teal-600 mt-0.5 flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <p className="text-sm text-slate-600 leading-relaxed mt-4">
              This is generally better than seeing no movement at all.
            </p>
          </section>

          {/* Does this mean approval is coming? */}
          <section>
            <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-4">Does this mean approval is coming?</h2>
            <p className="text-sm text-slate-600 leading-relaxed mb-3">
              Sometimes yes, but not always.
            </p>
            <p className="text-sm text-slate-600 leading-relaxed mb-4">
              Some people receive an approval notice soon after this update. Others remain in this status for weeks or months before the next step happens.
            </p>
            <p className="text-sm text-slate-600 leading-relaxed mb-3">
              Every case is different. The timeline depends on:
            </p>
            <ul className="space-y-2 mb-4">
              {[
                "Your form type",
                "Field office workload",
                "Background checks",
                "Missing evidence",
                "Overall USCIS backlog",
              ].map((item) => (
                <li key={item} className="flex items-start gap-3 text-sm text-slate-600">
                  <span className="w-1.5 h-1.5 rounded-full bg-slate-400 mt-2 flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* What may happen next */}
          <section>
            <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-4">What may happen next</h2>
            <p className="text-sm text-slate-600 leading-relaxed mb-4">
              After this status, the next update could be:
            </p>
            <div className="space-y-2.5">
              {NEXT_STATUSES.map((s) => (
                <div key={s.label} className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 border border-slate-100">
                  <s.icon className={`h-4 w-4 flex-shrink-0 ${s.color}`} />
                  <span className="text-sm font-medium text-slate-700">{s.label}</span>
                </div>
              ))}
            </div>
            <p className="text-sm text-slate-500 mt-3 italic">
              That last option is frustrating, but common.
            </p>
          </section>

          {/* How long can it stay like this? */}
          <section>
            <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-4">How long can it stay like this?</h2>
            <p className="text-sm text-slate-600 leading-relaxed mb-3">
              There is no universal timeline.
            </p>
            <p className="text-sm text-slate-600 leading-relaxed mb-3">
              Some applicants see movement in a few days. Others wait several months.
            </p>
            <p className="text-sm text-slate-600 leading-relaxed">
              If your case is still within normal USCIS processing times, waiting is usually normal.
            </p>
          </section>

          {/* What should you do now? */}
          <section>
            <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-4">What should you do now?</h2>
            <p className="text-sm text-slate-600 leading-relaxed mb-4">
              Use this period wisely:
            </p>
            <div className="space-y-2.5">
              {[
                "Check your USCIS account regularly",
                "Watch your mail for notices",
                "Keep supporting documents ready",
                "Save screenshots of status updates",
                "Compare your case to posted processing times",
                "Stay calm unless you are outside normal timelines",
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3 text-sm text-slate-600">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-teal-100 text-teal-700 flex items-center justify-center text-xs font-semibold mt-0.5">
                    {i + 1}
                  </span>
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </section>

          {/* Common mistakes to avoid */}
          <section>
            <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-4">Common mistakes to avoid</h2>
            <div className="space-y-2.5">
              {[
                "Assuming approval is guaranteed",
                "Panicking after one update",
                "Filing duplicate inquiries too early",
                "Ignoring mailed notices",
                "Comparing your case to unrelated cases online",
              ].map((item) => (
                <div key={item} className="flex items-start gap-3 text-sm text-slate-600">
                  <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </section>

          {/* Final takeaway */}
          <section className="bg-teal-50 rounded-xl p-6 border border-teal-100" data-testid="ar-takeaway">
            <h2 className="text-lg font-bold text-slate-900 mb-2">Final takeaway</h2>
            <p className="text-sm text-slate-700 leading-relaxed">
              "Case Is Being Actively Reviewed" is usually a sign that something is happening behind the scenes. It often means progress, even if the next update does not come right away.
            </p>
            <p className="text-sm text-slate-700 leading-relaxed mt-2">
              Patience is often part of the immigration process.
            </p>
          </section>
        </div>
      </article>

      {/* FAQ */}
      <section className="py-12 bg-slate-50 border-y border-slate-100" data-testid="ar-faq">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-6">FAQ</h2>
          <div className="space-y-2">
            {FAQS.map((faq) => (
              <FAQItem key={faq.q} q={faq.q} a={faq.a} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-14 md:py-18" data-testid="ar-cta">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <p className="text-base md:text-lg text-slate-700 leading-relaxed mb-6">
            Understand your USCIS updates in plain English and track future changes for free with ImmigrationGuide.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to={lp("/auth?mode=register")}
              className="inline-flex items-center justify-center gap-2 bg-teal-700 hover:bg-teal-800 text-white font-semibold px-6 py-3 rounded-lg text-sm transition-colors"
              data-testid="ar-cta-track"
            >
              Track My Case Free <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to={lp("/auth?mode=register")}
              className="inline-flex items-center justify-center gap-2 border border-slate-300 hover:bg-slate-50 text-slate-700 font-medium px-6 py-3 rounded-lg text-sm transition-colors"
              data-testid="ar-cta-explain"
            >
              Explain My Status
            </Link>
            <Link
              to={lp("/auth?mode=register")}
              className="inline-flex items-center justify-center gap-2 border border-slate-300 hover:bg-slate-50 text-slate-700 font-medium px-6 py-3 rounded-lg text-sm transition-colors"
              data-testid="ar-cta-alerts"
            >
              Get USCIS Alerts
            </Link>
          </div>
          <p className="mt-3 text-xs text-slate-500" data-testid="ar-cta-helper">Free account. No legal jargon. Easy status tracking.</p>
        </div>
      </section>

      {/* Related Guides */}
      <section className="py-10 border-t border-slate-100" data-testid="ar-related">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-lg font-bold text-slate-900 mb-4">Related Guides</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Link
              to={lp("/pricing")}
              className="group p-4 rounded-lg border border-slate-200 hover:border-teal-200 hover:shadow-sm transition-all"
              data-testid="ar-link-pricing"
            >
              <h3 className="font-semibold text-sm text-slate-800 group-hover:text-teal-700 transition-colors mb-1">
                Plans & Pricing
              </h3>
              <p className="text-xs text-slate-500">Unlock premium tools and AI-powered case tracking</p>
            </Link>
            <Link
              to={lp("/gc/green-card-timeline")}
              className="group p-4 rounded-lg border border-slate-200 hover:border-teal-200 hover:shadow-sm transition-all"
              data-testid="ar-link-timeline"
            >
              <h3 className="font-semibold text-sm text-slate-800 group-hover:text-teal-700 transition-colors mb-1">
                Green Card Processing Times
              </h3>
              <p className="text-xs text-slate-500">Realistic timelines by category for 2026</p>
            </Link>
            <Link
              to={lp("/gc/documents-checklist")}
              className="group p-4 rounded-lg border border-slate-200 hover:border-teal-200 hover:shadow-sm transition-all"
              data-testid="ar-link-docs"
            >
              <h3 className="font-semibold text-sm text-slate-800 group-hover:text-teal-700 transition-colors mb-1">
                Documents Checklist
              </h3>
              <p className="text-xs text-slate-500">Every document you need, organized by category</p>
            </Link>
            <Link
              to={lp("/gc/faq")}
              className="group p-4 rounded-lg border border-slate-200 hover:border-teal-200 hover:shadow-sm transition-all"
              data-testid="ar-link-faq"
            >
              <h3 className="font-semibold text-sm text-slate-800 group-hover:text-teal-700 transition-colors mb-1">
                Green Card FAQ
              </h3>
              <p className="text-xs text-slate-500">19 common questions answered in plain English</p>
            </Link>
          </div>
        </div>
      </section>

      {/* Trust Block */}
      <section className="py-6 bg-slate-50 border-t border-slate-100">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <div className="flex items-center justify-center gap-2 text-xs text-slate-500">
            <Shield className="h-3.5 w-3.5 text-teal-600" />
            <span>Last updated: April 2026 · Based on publicly available USCIS information. Educational content only. Not legal advice.</span>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
